<template>
  <div
    ref="containerRef"
    class="relative overflow-y-scroll lg:w-full"
    :class="{
      'shadow-vertical-scroll-both': routeScroll.showTop && routeScroll.showBottom,
      'shadow-vertical-scroll-down': routeScroll.showTop && !routeScroll.showBottom,
      'shadow-vertical-scroll-up': routeScroll.showBottom && !routeScroll.showTop,
    }"
    @scroll="onScroll($event)"
  >
    <div
      v-for="(stop, sIndex) in scheduleStops"
      :key="stop.id + sIndex"
      :ref="(el) => generateStopRef(el, stop)"
      class="relative px-4"
      :class="{ 'bg-green-700': stop.isSelected }"
    >
      <!-- Path -->
      <div
        class="z-20 flex flex-col justify-center items-center absolute top-[59px] ml-8"
        aria-hidden="true"
        :class="{ 'h-full': sIndex < scheduleStops.length - 1 }"
      >
        <div
          v-if="sIndex === 0"
          class="h-[100px] -mt-[100px] w-0.5 border-r-2 border-primary"
        ></div>
        <div class="w-3 h-3 bg-gray-100 border-2 rounded-full border-primary"></div>
        <div
          v-if="sIndex < scheduleStops.length - 1"
          class="flex-grow w-0.5 border-r-2 border-primary"
        ></div>
      </div>

      <!-- Button -->
      <div class="inline-block w-full p-2 pl-16 space-y-2">
        <div class="flex flex-col items-left">
          <div class="flex div">
            <h4 class="text-xl font-bold" :class="{ 'text-gray-700': !stop.selectedTime }">
              {{ stop.name }}&nbsp;
            </h4>
          </div>

          <div class="-ml-4" :class="{ 'z-50': stopListboxIndex === sIndex }">
            <Listbox
              :modelValue="stop.selectedTime?.value"
              @update:modelValue="(value) => setSelectedTime(value, stop)"
            >
              <div class="flex w-full">
                <div class="flex items-center mr-2">
                  <WithTooltip v-if="stop.hasAlerts" :id="`stopAlert_${stop.code}`">
                    <template v-slot:element="{ elementAttrs, show, hide }">
                      <Icon
                        :icon="mdiAlert"
                        class="w-8 h-8 text-secondary"
                        v-bind="elementAttrs"
                        tabindex="0"
                        @mouseenter="(e) => e.target.focus() && show()"
                        @focus="show"
                        @keyup.esc="(e) => e.target.blur() && hide()"
                        @blur="hide"
                      />
                    </template>
                    <template v-slot:tooltip>
                      Check Rider Alert above for more information
                    </template>
                  </WithTooltip>
                  <Icon
                    v-else
                    viewBox="5 0 24 24"
                    class="w-8 h-8 text-primary"
                    aria-hidden="true"
                    :icon="mdiTimelineClockOutline"
                    aria-label="Clock icon"
                  />
                </div>
                <ListboxButton
                  class="relative flex items-center w-full text-2xl text-left bg-transparent border border-gray-200 rounded hover:bg-white hover:hover-primary hover:shadow"
                  :class="{ 'opacity-50': stop.disabled }"
                  :aria-label="stop.listBoxLabel"
                >
                  <span class="flex items-center flex-1 min-w-0 p-3 pr-8 overflow-hidden">
                    <span
                      class="block min-w-0 text-xl truncate"
                      :class="{
                        'text-gray-900': ! stop.isCrossedOut,
                        'text-gray-800 line-through decoration-primary-gray-800': stop.isCrossedOut,
                      }"
                    >
                      {{ stop.label }}
                    </span>
                    <span class="flex gap-1">
                      <span
                        v-if="!stop.isCrossedOut && stop.selectedTime?.nextRide"
                        class="ml-2 p-1 text-xs font-normal rounded bg-primary-900 text-primary-100 whitespace-nowrap"
                      >Next Arrival</span>
                      <span
                        v-else-if="!stop.isCrossedOut && stop.noTime"
                        class="p-1 text-xs font-normal rounded bg-primary-900 text-primary-100 whitespace-nowrap"
                      >Ends at {{ getLastServedStopName() }}</span>
                      <span
                        v-else-if="!stop.isCrossedOut && stop.isLastServed"
                        class="ml-2 p-1 text-xs font-normal rounded bg-[#C3102F] text-white whitespace-nowrap"
                      >Last Stop</span>
                      <span
                        v-else-if="!stop.isCrossedOut && stop.isBeforeLastServed"
                        class="ml-2 p-1 text-xs font-normal rounded bg-[#BB481B] text-white whitespace-nowrap"
                      >Ends Early</span>
                    </span>
                  </span>
                  <span class="absolute inset-y-0 right-3 flex items-center w-5 pointer-events-none">
                    <Icon
                      class="w-5 h-5"
                      aria-hidden="true"
                      :icon="mdiChevronDown"
                      aria-label="Arrow down icon"
                    />
                  </span>
                </ListboxButton>
                <ListboxOptions
                  class="absolute z-20 w-auto py-1 ml-10 overflow-auto text-2xl bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-base"
                >
                  <ListboxOption
                    v-for="time in stop.times"
                    v-slot="{ active, selected }"
                    :key="time.value"
                    :value="time.value"
                    :disabled="!time.enabled"
                    as="template"
                  >
                    <li
                      :class="{
                        'flex items-center select-none relative p-2 z-20': true,
                        'bg-green-700': active,
                        'text-primary-900 bg-secondary-200': !active && selected,
                        'text-black': (!active || !selected) && time.enabled,
                        'text-gray-800 line-through decoration-primary-gray-800': !time.enabled,
                        'cursor-pointer': time.enabled,
                      }"
                      :aria-label="`${stop.name} - ${time.label}${
                        time.enabled && shouldShowShortTripIndicator(stop) && time.isLastServedStop ? ' - Last Stop' : ''
                      }${
                        time.enabled && shouldShowShortTripIndicator(stop) && time.isStopBeforeLastServed ? ' - Ends Early' : ''
                      }${
                        time.enabled && time.nextRide ? ' - Next Arrival' : ''
                      }`"
                    >
                      <span class="flex items-center w-6 pr-3 text-primary-600">
                        <Icon
                          v-if="selected"
                          class="w-5 h-5"
                          aria-role="checkbox"
                          aria-checked="true"
                          :icon="mdiCheck"
                          aria-label="Check icon"
                        />
                      </span>
                      <span
                        :class="[
                          selected ? 'font-medium' : 'font-normal',
                          'flex-1 flex items-center justify-between z-20',
                        ]"
                      >
                        <span class="block truncate">{{ time.label }}</span>
                        <span class="flex gap-1">
                          <span
                            v-if="time.enabled && time.nextRide"
                            class="z-20 p-1 text-xs font-normal rounded bg-primary-900 text-primary-100 whitespace-nowrap ml-2"
                            >Next Arrival</span
                          >
                          <span
                            v-else-if="time.enabled && shouldShowShortTripIndicator(stop) && time.isLastServedStop"
                            class="z-20 p-1 text-xs font-normal rounded bg-[#C3102F] text-white whitespace-nowrap"
                            >Last Stop</span
                          >
                          <span
                            v-else-if="time.enabled && shouldShowShortTripIndicator(stop) && time.isStopBeforeLastServed"
                            class="z-20 p-1 text-xs font-normal rounded bg-[#BB481B] text-white whitespace-nowrap"
                            >Ends Early</span
                          >
                        </span>
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          <div class="flex mt-2 text-sm font-normal text-gray-900 div">Stop #{{ stop.code }}</div>
        </div>

        <div v-if="stop.selectedTime !== ''" class="flex items-center justify-between w-full py-2">
          <div v-if="stop.headsign" class="flex items-center py-6 text-primary">
            <div class="flex flex-row pl-2 -mb-2 -ml-20">
              <Icon
                class="w-8 h-8 mt-8 -mr-2"
                :icon="mdiHail"
                :aria-label="`Headsign: ${routeStore.filters.directionOfTravel.headsignLabel} to ${stop.headsign}`"
              ></Icon>
              <div class="w-4 mt-6 overflow-hidden">
                <Icon
                  class="w-12 h-12"
                  :icon="mdiBusStop"
                  aria-hidden="true"
                  aria-label="Bus stop icon"
                ></Icon>
              </div>
              <Icon
                v-if="ICONS[currentRoute.group.mode]"
                class="ml-1 w-14 h-14"
                :icon="ICONS[currentRoute.group.mode]"
                aria-hidden="true"
                :aria-label="`Current route is a ${currentRoute.group.mode}`"
              ></Icon>
            </div>
            <div
              class="flex items-center w-full p-1 pt-0 pb-0 -ml-4 space-x-2 text-base font-normal scale-75 rounded-lg sm:ml-0 sm:scale-100 bg-primary text-primary-50"
            >
              <div class="text-4xl text-left" :class="{ hidden: displayRouteAsText(currentRoute) }">
                <strong>{{ currentRoute.id }}</strong>
              </div>
              <div class="flex-1 text-2xl text-center whitespace-nowrap">
                <span :class="[displayRouteAsText(currentRoute) ? 'text-2xl' : 'text-base']">
                  <span :class="{ hidden: !displayRouteAsText(currentRoute) }">
                    {{ currentRoute.id }}
                  </span>
                  {{ routeStore.filters.directionOfTravel.headsignLabel }}
                  <span :class="{ hidden: displayRouteAsText(currentRoute) }">to</span>
                </span>
                <br />
                {{ stop.headsign }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { usePage } from '@inertiajs/vue3';
import {
  mdiAlert,
  mdiBusStop,
  mdiCheck,
  mdiChevronDown,
  mdiHail,
  mdiTimelineClockOutline,
} from '@mdi/js';
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue';
import { ICONS } from '../../utils/icons';
import Icon from '../Icon';
import InputSelect from '../InputSelect';
import LightButton from '../LightButton';
import Spinner from '../Spinner';
import WithTooltip from '../WithTooltip.vue';
import { useRouteStore } from '../../stores/routes';

const SHORT_TRIP_INDICATOR_EXCEPTIONS = {
  routes: ['61s'],
  timepointsByRoute: {
    a: [
      {
        includes: ['washington', 'central ave'],
      },
    ],
  },
};

export default defineComponent({
  name: 'MapsSchedulesRouteScheduleList',
  components: {
    Icon,
    Spinner,
    InputSelect,
    LightButton,
    Listbox,
    ListboxOption,
    ListboxOptions,
    ListboxButton,
    WithTooltip,
  },
  props: {
    schedule: { type: Object, required: true },
  },

  setup(props) {
    const routeStore = useRouteStore();

    const currentRoute = computed(() => routeStore.routes.current);
    const routeScroll = computed(() => routeStore.routes.scroll);
    const isCustomDayOfTravel = computed(
      () => routeStore.filters.dayOfTravel.selectedDate !== null
    );
    const stopListboxIndex = ref(-1);
    const containerRef = ref(null);
    const stopsRefs = ref({});

    const selectedStopCode = ref('');
    const selectedStopTime = ref({});

    const scheduleStops = computed(() => {
      const stops = usePage().props.global.feature_flags.maps_and_schedules_group_stops_by_area
        ? props.schedule.stopsByArea
        : props.schedule.stops;

      return stops.map(stop => {
        const label = getLabel(stop);
        const noTime = isStopWithNoTime(stops, stop);

        return ({
          ...stop,
          label: label,
          listBoxLabel: getListboxLabel(stops, stop),
          isSelected: isStopSelected(stop),
          isCrossedOut: !stop.selectedTime?.enabled && label !== 'See Rider Alert' && !noTime,
          isLastServed: isLastServedStop(stop),
          isBeforeLastServed: isStopBeforeLastServed(stop),
          noTime: noTime,
        });
      })
    });

    // Check if we should show short trip indicators based on feature flag and route
    const shouldShowShortTripIndicators = computed(() => {
      const featureFlag = usePage().props.global.feature_flags?.short_trip_indicator;
      return !!featureFlag;
    });

    function normalizeRouteId(routeId) {
      return routeId?.toString().trim().toLowerCase();
    }

    function normalizeStopName(stopName) {
      return stopName?.toString().trim().toLowerCase().replace(/\s+/g, ' ');
    }

    function matchesTimepointException(stop, exception) {
      const stopName = normalizeStopName(stop?.name);

      if (!stopName || !exception?.includes?.length) return false;

      return exception.includes.every((value) => stopName.includes(value));
    }

    function shouldShowShortTripIndicator(stop) {
      if (!shouldShowShortTripIndicators.value) return false;

      const routeId = normalizeRouteId(currentRoute.value?.id);

      if (SHORT_TRIP_INDICATOR_EXCEPTIONS.routes.includes(routeId)) {
        return false;
      }

      const timepointExceptions =
        SHORT_TRIP_INDICATOR_EXCEPTIONS.timepointsByRoute[routeId] || [];

      if (timepointExceptions.some((exception) => matchesTimepointException(stop, exception))) {
        return false;
      }

      return true;
    }

    // Check if the selected trip is a short trip based on selectedTime
    const isShortTrip = computed(() => {
      if (!shouldShowShortTripIndicators.value) return false;
      if (!scheduleStops.value || scheduleStops.value.length === 0) return false;

      // Check if any stop has selectedTime with isShortTrip flag
      return scheduleStops.value.some(stop => stop.selectedTime?.isShortTrip);
    });

    const getActualStopNameForSelectedTrip = (stop) => {
      if (!stop) return '';

      const groupedByAreaEnabled =
        usePage().props.global.feature_flags.maps_and_schedules_group_stops_by_area;

      if (!groupedByAreaEnabled || !stop.selectedTime?.value || !stop.stops?.length) {
        return stop.name;
      }

      const servedStop = stop.stops.find((childStop) => {
        return childStop.times?.some(
          (time) => time.value === stop.selectedTime.value && time.date
        );
      });

      return servedStop?.name || stop.name;
    };

    // Select the first NextRide by default.
    nextTick(() => {
      if (scheduleStops.value) {
        routeStore.setTimeOfTravelValue(
          routeStore.getNextArrivalFromStops(scheduleStops.value)?.value
        );
      }
    });

    function stopListboxToggled(index) {
      stopListboxIndex.value = stopListboxIndex.value !== index ? index : -1;
    }

    function highlightStop(stop) {
      routeStore.setSelectedStops([stop]);
    }

    function isStopSelected(stop) {
      return usePage().props.global.feature_flags.maps_and_schedules_group_stops_by_area
        ? stop.stops.some((s) => routeStore.isStopSelected(s))
        : routeStore.isStopSelected(stop);
    }

    function displayRouteAsText(route) {
      return route.id === '68CM' && route.group.mode !== 'bus' && !(parseFloat(route.id) + 1);
    }

    function generateStopRef(el, stop) {
      stopsRefs.value[stop.code] = el;
    }

    function onScroll(event) {
      const showTop = event.target.scrollTop > 15;
      const showBottom =
        event.target.scrollTop < event.target.scrollHeight - event.target.clientHeight - 20;

      routeStore.setRouteScroll(showTop, showBottom);
    }

    function getLabel(stop) {
      if (
        isCustomDayOfTravel.value &&
        stop.hasAlerts &&
        (stop.disabled || (stop.selectedTime && !stop.selectedTime.enabled))
      ) {
        return 'See Rider Alert';
      }

      return stop.selectedTime ? stop.selectedTime.label : '';
    }

    function isStopWithNoTime(stops, stop) {
      // Only show if feature flag is enabled and this route/timepoint is not excepted.
      if (!shouldShowShortTripIndicator(stop)) return false;

      // Don't show if the selectedTime is disabled (crossed out)
      if (stop.selectedTime && !stop.selectedTime.enabled) return false;

      // Check if the stop has no selectedTime (train won't stop here for selected trip)
      if (!stop.selectedTime) {
        // Use stopIndex if available, otherwise find the index
        const stopIndex = stop.stopIndex !== undefined ? stop.stopIndex : stops.findIndex(s => s.code === stop.code);
        if (stopIndex === -1) return false;

        // Check if all stops after this also have no selectedTime (confirming this is at the end)
        const stopsAfter = stops.slice(stopIndex + 1);
        const allAfterHaveNoTime = stopsAfter.length === 0 || stopsAfter.every(s => !s.selectedTime);

        return allAfterHaveNoTime;
      }
      return false;
    }

    function getLastServedStopName() {
      // Fallback: Find the last stop with a selectedTime
      if (scheduleStops.value && scheduleStops.value.length > 0) {
        // Find the last stop that has a selectedTime
        for (let i = scheduleStops.value.length - 1; i >= 0; i--) {
          const stop = scheduleStops.value[i];
          if (stop.selectedTime) {
            const stopNameFromScheduleList = scheduleStops.value.find(
              (scheduleStop) => scheduleStop.id?.toString() === stop.id?.toString()
            )?.name;

            return stopNameFromScheduleList || getActualStopNameForSelectedTrip(stop);
          }
        }
      }

      return 'Previous Stop';
    }

    function isLastServedStop(stop) {
      // Check if the selectedTime for this stop is marked as the last served stop
      if (!shouldShowShortTripIndicator(stop)) return false;
      // Don't show if the time is disabled (crossed out)
      if (!stop.selectedTime?.enabled) return false;
      return stop.selectedTime?.isLastServedStop || false;
    }

    function isStopBeforeLastServed(stop) {
      // Check if the selectedTime for this stop is marked as before the last served stop
      if (!shouldShowShortTripIndicator(stop)) return false;
      // Don't show if the time is disabled (crossed out)
      if (!stop.selectedTime?.enabled) return false;
      return stop.selectedTime?.isStopBeforeLastServed || false;
    }
    function setSelectedTime(value, stop) {
      const stopTime = _.find(stop.times, (time) => time.value === value);
      selectedStopCode.value = stopTime ? stop.code : '';
      selectedStopTime.value = stopTime || {};
      routeStore.setTimeOfTravelValue(value);
    }

    const getListboxLabel = (stops, stop) => {
      let label = stop.name;

      if (isStopWithNoTime(stops, stop)) {
        label += ` - Ends at ${getLastServedStopName()}`;
      } else {
        const selectedTime =
          stop.code === selectedStopCode.value ? selectedStopTime.value : stop.selectedTime;
        label += ` - ${selectedTime?.label || ''}`;

          if (selectedTime?.nextRide) {
              label += ' - Next Arrival';
          } else if (isLastServedStop(stop)) {
            label += ' - Last Stop';
          } else if (isStopBeforeLastServed(stop)) {
              label += ' - Ends Early';
          }
      }

      return label;
    };

    onMounted(async () => {
      await routeStore.initStore();

      if (routeStore.selectedStops.length > 0) {
        const stopRef = stopsRefs.value[routeStore.selectedStops[0].code];

        if (stopRef) {
          containerRef.value.scroll({ top: stopRef.offsetTop });
        }
      }
    });

    return {
      routeStore,
      stopsRefs,
      containerRef,
      routeScroll,
      currentRoute,
      stopListboxIndex,
      scheduleStops,
      shouldShowShortTripIndicators,
      isShortTrip,

      // Methods
      onScroll,
      isStopSelected,
      isLastServedStop,
      isStopBeforeLastServed,
      shouldShowShortTripIndicator,
      getLastServedStopName,
      displayRouteAsText,
      generateStopRef,
      setSelectedTime,
      getListboxLabel,

      // Icons
      ICONS,
      mdiAlert,
      mdiCheck,
      mdiChevronDown,
      mdiHail,
      mdiBusStop,
      mdiTimelineClockOutline,
    };
  },
});
</script>
